import { CdkTree } from '@angular/cdk/tree';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEmpty } from 'lodash';
import { BehaviorSubject, delay } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { RootFocusedAnnotationService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/root-focused-annotation.service';
import { RootPushAndTrackService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/root-push-and-track.service';
import { ObjectClassService } from '../../../../../../../core/shared/misc-services/object-class.service';
import { DebouncedEventTracker } from '../../../../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { EventTrackingService } from '../../../../../../../shared/event-tracking/event-tracking.service';
import {
  ClassSelectorService,
  ClassSelectorTreeNode,
  isClassNode,
} from './class-selector.service';

@Component({
  selector: 'h3-class-selector',
  templateUrl: './class-selector.component.html',
  styleUrls: ['./class-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClassSelectorService],
  standalone: false,
})
export class ClassSelectorComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  @ViewChild(CdkTree, { static: true })
  tree!: CdkTree<ClassSelectorTreeNode>;
  @Output() closeClassSelector = new EventEmitter<string | undefined>();
  @ViewChild('filterInput') filterInput!: ElementRef;
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly rootFocusedAnnotationService = inject(
    RootFocusedAnnotationService,
  );
  private readonly objectClassService = inject(ObjectClassService);
  private readonly pushAndTrackService = inject(RootPushAndTrackService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly classSelectorService = inject(ClassSelectorService);

  dataSource$ = this.classSelectorService.getNodes$();
  isSearchFocused$ = new BehaviorSubject(false);
  selectedClass$ = this.rootFocusedAnnotationService.getClass$();
  selectedClassLabel$ = this.selectedClass$.pipe(
    map(
      (className) =>
        this.objectClassService.getClassLabel(className) ?? 'Unspecified class',
    ),
  );
  highlightedClassId$ = this.classSelectorService.highlightedNodeId$;
  classFilter = '';
  focusedObjectIndex$ = this.rootFocusedAnnotationService
    .getAnnotationStore$()
    .pipe(
      map((store) => store?.state()),
      filter(isDefined),
      map((annotation) => annotation.index),
    );

  private debouncedEventTracker = new DebouncedEventTracker(
    { id: 'filter_classes', bucket: 'tag_selection', sub_bucket: 'class' },
    this.eventTrackingService,
  );

  constructor() {
    super();
    this.subscribeTo(this.selectedClass$, (className) =>
      this.classSelectorService.selectClass(className),
    );
  }

  ngAfterViewInit() {
    this.filterInput.nativeElement.focus();

    // delay(0) ensure the nodes are loaded in the tree
    this.subscribeTo(this.dataSource$.pipe(delay(0)), () => {
      this.classSelectorService.getAllNodes().forEach((node) => {
        if (node.isExpanded) {
          this.tree.expand(node);
        } else {
          this.tree.collapse(node);
        }
      });
    });
  }

  childrenAccessor = (node: ClassSelectorTreeNode) =>
    this.classSelectorService.getNodes(node.id);

  isGroupTaxonomy = (_: number, node: ClassSelectorTreeNode) =>
    !isClassNode(node);

  updateClassAndClose(className: string, source = 'mouse') {
    const classStore = this.rootFocusedAnnotationService.getClassStore();
    classStore.set(className);
    this.taskWorkspaceStore('nextObjectClass').set(className);

    this.pushAndTrackService.pushAndTrack({
      id: 'select_class',
      bucket: 'tag_selection',
      sub_bucket: 'annotation',
      className,
      source,
    });
    this.close();
  }

  close(source?: string) {
    this.closeClassSelector.emit(source);
  }

  filterClasses() {
    this.classSelectorService.filterNodes(this.classFilter);
    if (!isEmpty(this.classFilter)) {
      this.debouncedEventTracker.trackWithDebounce({
        className: this.classFilter,
      });
    }
  }

  onSearchFocusIn() {
    this.isSearchFocused$.next(true);
  }

  onSearchFocusOut() {
    this.isSearchFocused$.next(false);
  }

  onSearchKeyDownEnter(event: Event) {
    event.stopPropagation();
    const highlightedClass = this.classSelectorService.getHighlightedClass();
    if (!highlightedClass) {
      this.close();
      return;
    }

    this.updateClassAndClose(highlightedClass, 'keyboard_shortcut');
  }

  onGroupNodeExpanded(node: ClassSelectorTreeNode) {
    this.eventTrackingService.track({
      id: 'toggle_taxonomy_group',
      bucket: 'tag_selection',
      sub_bucket: 'class',
      group: node.label,
      mode: this.classSelectorService.toggleIsExpanded(node.id)
        ? 'expand'
        : 'collapse',
    });
  }
}
